import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LocationProvider } from './contexts/LocationContext';
import Login from './pages/Login';
import Home from './pages/Home';
import KioskDetail from './pages/KioskDetail';
import KioskQRCode from './pages/KioskQRCode';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderTracking from './pages/OrderTracking';
import Orders from './pages/Orders';
import Profile from './pages/Profile';
import MapView from './pages/MapView';
import Coupons from './pages/Coupons';
import Cashback from './pages/Cashback';
import BeachInfo from './pages/BeachInfo';
import Referral from './pages/Referral';
import Loyalty from './pages/Loyalty';
import ReviewOrder from './pages/ReviewOrder';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import CityHallDashboard from './pages/admin/CityHallDashboard';
import BrandDashboard from './pages/brand/BrandDashboard';
import LoadingSpinner from './components/common/LoadingSpinner';
import ChatBot from './components/common/ChatBot';
import ReferralCapture from './components/common/ReferralCapture';
import PushPermissionPrompt from './components/common/PushPermissionPrompt';
import InstallPWAPrompt from './components/common/InstallPWAPrompt';

import './App.css';

const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><LoadingSpinner size="lg" /></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && user && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><LoadingSpinner size="lg" /></div>;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  return (
    <>
      <ReferralCapture />
      <Routes>
        <Route path="/partner/dashboard" element={<ProtectedRoute allowedRoles={['kiosk_owner', 'kiosk_staff', 'admin']}><PartnerDashboard /></ProtectedRoute>} />
        <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/cityhall/dashboard" element={<ProtectedRoute allowedRoles={['city_hall', 'admin']}><CityHallDashboard /></ProtectedRoute>} />
        <Route path="/brand/dashboard" element={<ProtectedRoute allowedRoles={['brand', 'admin']}><BrandDashboard /></ProtectedRoute>} />

        <Route
          path="*"
          element={
            <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
              <Routes>
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/map" element={<ProtectedRoute><MapView /></ProtectedRoute>} />
                <Route path="/kiosk/:id" element={<ProtectedRoute><KioskDetail /></ProtectedRoute>} />
                <Route path="/kiosk/:id/qr" element={<ProtectedRoute><KioskQRCode /></ProtectedRoute>} />
                <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                <Route path="/order/:id" element={<ProtectedRoute><OrderTracking /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/coupons" element={<ProtectedRoute><Coupons /></ProtectedRoute>} />
                <Route path="/cashback" element={<ProtectedRoute><Cashback /></ProtectedRoute>} />
                <Route path="/beach-info" element={<ProtectedRoute><BeachInfo /></ProtectedRoute>} />
                <Route path="/referral" element={<ProtectedRoute><Referral /></ProtectedRoute>} />
                <Route path="/loyalty" element={<ProtectedRoute><Loyalty /></ProtectedRoute>} />
                <Route path="/review/:orderId" element={<ProtectedRoute><ReviewOrder /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <ChatBot />
              <PushPermissionPrompt />
              <InstallPWAPrompt />
            </div>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <LocationProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors closeButton />
          </LocationProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
